import { breakpoint } from '@/helpers/BreakpointHelper';
import React, { FC, ImgHTMLAttributes, useState } from 'react';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/scrollbar/scrollbar.min.css';
import 'swiper/swiper.min.css';
import ButtonMoreView from '../ButtonMoreView';
import EndviolenceLayerPopup from '../EndviolenceLayerPopup';
import Image from '../Image';
import LinkSafe from '../LinkSafe';
import SwiperBasic from '../Swiper/SwiperBasic';
import { Tit } from '../Titles';
import Youtube from '@/components/Youtube';
import styled from 'styled-components';
import SwiperCore, { A11y, Navigation, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

SwiperCore.use([Navigation, Scrollbar, A11y]);

interface CampaignProps {
  campaigns: CampaignData[];
  img?: boolean;
  overflowHidden?: boolean;
  locale?: 'ko' | 'en';
}

export interface CampaignData {
  id: string | number;
  pcImage: ImgHTMLAttributes<any>['src'];
  mobileImage: ImgHTMLAttributes<any>['src'];
  image: ImgHTMLAttributes<any>['src'];
  title?: string;
  link?: string;
  pop?: string;
  dec?: string;
  youtube?: string;
  thumYoutube?: string;
}

const CardLayout = styled.div`
  .dec-wrap {
    padding-top: 16px;
    ${Tit} {
      line-height: 1.6;
      min-height: 76px;
      ${breakpoint(800)} {
        min-height: 52px;
      }
      ${breakpoint(640)} {
        min-height: auto;
      }
    }
    .dec {
      margin-top: 16px;
      min-height: 128px;
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      ${breakpoint(800)} {
        min-height: 114px;
      }
      ${breakpoint(640)} {
        min-height: auto;
      }
    }
    .yuotube-wrap {
      margin-top: 16px;
      line-height: 2;
      height: 32px;
    }
    ${ButtonMoreView} {
      margin-top: 32px;
      max-width: 214px;
      width: 100%;
    }

    ${breakpoint(`tablet`)} {
      padding-left: 20px;
      padding-right: 20px;

      ${ButtonMoreView} {
        margin-top: 72px;
        max-width: 148px;
      }
    }
  }
`;
const CardImage = styled.div`
  overflow: hidden;
  position: relative;

  .img-box {
    width: 100%;
    padding-top: 57.14%;
  }

  .card-img {
    max-width: 100%;
    width: 100%;
  }
`;

const SwiperCustom = styled(SwiperBasic)`
  padding: 0;
  .swiper-container {
    overflow: hidden;

    .swiper-slide {
      overflow: hidden;
      width: 448px;
      margin-right: 32px;
    }
  }

  ${breakpoint(`tablet`)} {
    .swiper-container {
      margin-left: -20px;
      margin-right: -20px;
    }
  }

  ${breakpoint(`mobile`)} {
    .swiper-container {
      .swiper-slide {
        width: 100%;
        margin-right: 0 !important;
      }
    }
  }
`;

const CampaignCard2: FC<CampaignProps> = ({
  campaigns,
  img,
  locale = `ko`,
  allowTouchMove,
  ...props
}) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [PopupHeader, setPopupHeader] = useState(``);
  const [PopupImg, setPopupImg] = useState(``);
  const [PopupTitle, setPopupTitle] = useState(``);
  const [PopupDec, setPopupDec] = useState(``);
  const [PopupYoutube, setPopupYoutube] = useState(``);
  const [PopupThumYoutube, setPopupThumYoutube] = useState(``);
  const [PopupLocale, setPopupLocale] = useState(``);
  const [PopupSid, setPopupSid] = useState<PopupData | string | number>(``);

  const open = () => setPopupOpen(true);
  const close = () => setPopupOpen(false);
  return (
    <>
      <SwiperCustom {...props}>
        <Swiper slidesPerView="auto" navigation scrollbar={{ draggable: true }}>
          {campaigns.map((campaign) => (
            <SwiperSlide key={campaign.id}>
              <CardLayout>
                <CardImage>
                  {img ? (
                    <Image
                      className="card-img"
                      pcSrc={campaign.image}
                      mobileSrc={campaign.image}
                    />
                  ) : (
                    <>
                      {campaign.thumYoutube ? (
                        <Youtube
                          noneTab
                          className="youtube-container"
                          endViolence={campaign.thumYoutube}
                        />
                      ) : (
                        <div
                          className="img-box"
                          css={`
                            background-image: url(${campaign.image});
                          `}
                        />
                      )}
                    </>
                  )}
                </CardImage>
                <div className="dec-wrap">
                  <Tit size="s3-1">{campaign.title}</Tit>
                  {campaign.dec && (
                    <p
                      className="dec"
                      dangerouslySetInnerHTML={{ __html: campaign.dec }}
                    />
                  )}
                  <div className="yuotube-wrap">
                    {campaign.youtube ? (
                      <LinkSafe to={campaign.youtube} className="link-blank">
                        *{locale === `ko` ? `영상 확인하기` : `watch the video`}
                        :{` `}
                        {campaign.youtube}
                      </LinkSafe>
                    ) : (
                      ` `
                    )}
                  </div>
                  {campaign.link && (
                    <ButtonMoreView mode="sky">
                      {locale === `ko` ? `자세히 보기` : `Read more`}
                    </ButtonMoreView>
                  )}
                  {campaign.pop && (
                    <ButtonMoreView
                      mode="sky"
                      onClick={() => {
                        open();
                        setPopupHeader(campaign.pop);
                        setPopupSid(campaign.id);
                        setPopupImg(campaign.image);
                        setPopupTitle(campaign.title);
                        setPopupDec(campaign.dec);
                        setPopupYoutube(campaign.youtube);
                        setPopupThumYoutube(campaign.thumYoutube);
                        setPopupLocale(locale);
                      }}
                    >
                      {locale === `ko` ? `자세히 보기` : `Read more`}
                    </ButtonMoreView>
                  )}
                </div>
              </CardLayout>
            </SwiperSlide>
          ))}
        </Swiper>
      </SwiperCustom>
      <EndviolenceLayerPopup
        isOpen={popupOpen}
        PopupSid={PopupSid}
        PopupHeader={PopupHeader}
        PopupImg={PopupImg}
        PopupTitle={PopupTitle}
        PopupDec={PopupDec}
        PopupYoutube={PopupYoutube}
        PopupLocale={PopupLocale}
        PopupThumYoutube={PopupThumYoutube}
        onRequestClose={close}
      />
    </>
  );
};
export default CampaignCard2;
